// @flow

import qs from 'qs';
import {ApiError} from './ApiError';
import type {ApiRequest} from './ApiRequest';

// This is a low-level class that's used by the (code-genned) ApiClient. It does all of the
// actual fetch() calls, allowing ApiClient to be a lightweight facade. You should never need
// to interact directly with the ApiRequestExecutor; instead, go through the ApiClient.
export class ApiRequestExecutor {
  _baseUrl: string;

  _csrfToken: string;

  _routeMap: {[endpointId: string]: string};

  constructor(
    baseUrl: string,
    csrfToken: string,
    routeMap: {[endpointId: string]: string},
  ) {
    this._baseUrl = baseUrl;
    this._routeMap = routeMap;
    this._csrfToken = csrfToken;
  }

  async execute(request: ApiRequest): Promise<any> {
    const path = this._routeMap[request.endpointId];

    if (!path) {
      throw new Error(`Unknown Docs API endpoint ${request.endpointId}`);
    }

    const url = new URL(path, this._baseUrl);

    let response;
    if (request.method === 'get') {
      response = await this._get(url, request.payload);
    } else {
      response = await this._post(url, request.payload);
    }

    if (!response.ok) {
      throw new ApiError('Docs API endpoint returned an unexpected response');
    }

    // Some endpoints just return a 204 or otherwise don't have a meaningful response body.
    // In this case, just return the raw body text instead of trying to deserialize it.
    const contentType = response.headers.get('content-type');

    if (contentType !== 'application/json') {
      return response.text();
    } else {
      return response.json();
    }
  }

  async _get(url: URL, payload: any): Promise<Response> {
    if (payload) {
      url.search = qs.stringify(payload);
    }

    return fetch(url.toString(), {
      method: 'get',
    });
  }

  async _post(url: URL, payload: any): Promise<Response> {
    return fetch(url.toString(), {
      method: 'post',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        'X-Stripe-CSRF-Token': this._csrfToken,
      },
    });
  }
}
